import { Component } from 'preact';
import { motion, AnimatePresence } from 'framer-motion';
import { SlidersHorizontal, X } from 'lucide-react';
import { pushDismiss, removeDismiss } from '../utils/escStack';
import { SorceryElementIcon } from './CardFilterBar';
import { Select } from './ui/select';
import { Button } from './ui/button';
import { t, k } from '../localization/t';
import { isFreePlay } from '../utils/freePlay';
import { GOLD, TEXT_BODY, ACCENT_GOLD, CornerPlating } from '../lib/medievalTheme';

// Sampled once at module load. An OS-level setting; Electron restarts on change.
const REDUCED_MOTION = typeof window !== 'undefined'
  && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

// Same field family as the deck builder's advanced panel, so the two
// panels read as one component. Desktop-only: the phone gallery keeps
// its funnel sheet.
const FIELD_RADIUS = 'var(--radius-md)';
const PANEL_PAD = '0.625cqw';
const PANEL_RADIUS = 'var(--radius-2xl)';
const FIELD_STYLE = {
  height: '2cqw',
  borderRadius: FIELD_RADIUS,
  background: '#080604',
  border: `1px solid ${GOLD} 0.22)`,
  color: '#f4e4b8',
  boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.5)',
  textShadow: 'none',
};
const CAPTION_CLASS = 'block text-[0.5cqw] font-semibold mb-1';
const CAPTION_STYLE = { color: 'rgba(166, 160, 155, 0.6)' };
const PANEL_CHIP = '!text-[0.625cqw] !px-1.5';

const DEFAULT_SORTS = [
  { value: 'latest', label: k('Latest') },
  { value: 'name', label: k('Name') },
  { value: 'oldest', label: k('Oldest') },
];

function chipProps(active, extraClassName) {
  const className = extraClassName ? `filter-chip ${extraClassName}` : 'filter-chip';
  return { className, 'data-active': active ? 'true' : 'false' };
}

/**
 * The deck collection's search capsule + advanced-filters popover:
 * the same search-with-sliders-button capsule and gold panel as the
 * deck builder's CardFilterBar, holding the deck-level filters (sort,
 * type, elements, avatar, tag) that used to crowd the toolbar as
 * individual dropdowns.
 *
 * Fully controlled: filter state lives in DeckGallery (the phone
 * funnel sheet reads the same state), this component only renders it.
 *
 * The Public tab reuses it with `sortOptions` swapped for the library
 * sorts and `hideElements` / `hideTag` set: those two filters are
 * resolved client-side against the full collection, which the paged
 * library fetch doesn't have.
 */
export default class DeckAdvancedFilters extends Component {
  state = { open: false };

  componentWillUnmount() {
    this._teardown();
  }

  _teardown() {
    if (this._escId != null) { removeDismiss(this._escId); this._escId = null; }
    document.removeEventListener('mousedown', this.handleOutsideDown, true);
  }

  setOpen(open) {
    if (open === this.state.open) return;
    this.setState({ open });
    if (open) {
      this._escId = pushDismiss(() => this.setOpen(false));
      document.addEventListener('mousedown', this.handleOutsideDown, true);
    } else {
      this._teardown();
    }
  }

  handleOutsideDown = (e) => {
    if (this._rootEl?.contains(e.target)) return;
    // Portaled Select menus render into document.body; a click inside
    // one must not close the whole popover.
    if (e.target?.closest?.('[role="menu"]')) return;
    this.setOpen(false);
  };

  activeCount() {
    const { avatar, tag, type, elements } = this.props;
    return (avatar ? 1 : 0)
      + (tag !== 'all' ? 1 : 0)
      + (type !== 'all' ? 1 : 0)
      + (elements?.size || 0);
  }

  handleReset = () => {
    const p = this.props;
    p.onAvatarChange?.(null);
    p.onTagChange?.('all');
    p.onTypeChange?.('all');
    p.onSortChange?.((p.sortOptions || DEFAULT_SORTS)[0].value);
    p.onElementsChange?.(new Set());
  };

  toggleElement(name) {
    const next = new Set(this.props.elements || []);
    if (next.has(name)) next.delete(name);
    else next.add(name);
    this.props.onElementsChange?.(next);
  }

  renderPanel() {
    const {
      sort, type, tag, avatar, elements, avatarOptions = [], tagOptions = [], onManageTags,
      sortOptions = DEFAULT_SORTS, hideElements = false, hideTag = false, title = t('Deck Filters'),
    } = this.props;

    const fieldLabel = (label) => (
      <div className={CAPTION_CLASS} style={CAPTION_STYLE}>{label}</div>
    );
    const chipRow = (label, options, value, onChange) => (
      <div>
        {fieldLabel(label)}
        <div className="flex flex-wrap items-center gap-1">
          {options.map((opt) => (
            <button
              key={opt.value}
              type="button"
              {...chipProps(value === opt.value, PANEL_CHIP)}
              onClick={() => onChange(opt.value)}
            >
              {t(opt.label)}
            </button>
          ))}
        </div>
      </div>
    );

    return (
      <motion.div
        key="deck-filters-panel"
        initial={REDUCED_MOTION ? { opacity: 0 } : { opacity: 0, scale: 0.98, y: -6 }}
        animate={REDUCED_MOTION ? { opacity: 1 } : { opacity: 1, scale: 1, y: 0 }}
        exit={REDUCED_MOTION
          ? { opacity: 0, transition: { duration: 0.1 } }
          : { opacity: 0, scale: 0.985, y: -4, transition: { duration: 0.12, ease: [0.22, 1, 0.36, 1] } }}
        transition={{ duration: 0.16, ease: [0.22, 1, 0.36, 1] }}
        className="absolute right-0 mt-2 z-50"
        style={{
          top: '100%',
          width: '26cqw',
          // Right-aligned with the capsule, growing from its sliders
          // button (which sits at the capsule's right end).
          transformOrigin: 'top right',
          borderRadius: PANEL_RADIUS,
          background: 'linear-gradient(180deg, #0e0b08 0%, #080604 100%)',
          border: `1px solid ${GOLD} 0.42)`,
          boxShadow: 'inset 0 1px 0 rgba(255,220,140,0.1), inset 0 0 40px rgba(0,0,0,0.4), 0 10px 34px rgba(0,0,0,0.7)',
        }}
      >
        <CornerPlating position="top-left" color={`${GOLD} 0.6)`} radius={PANEL_RADIUS} size="1.5cqw" />
        <CornerPlating position="top-right" color={`${GOLD} 0.6)`} radius={PANEL_RADIUS} size="1.5cqw" />
        <CornerPlating position="bottom-left" color={`${GOLD} 0.6)`} radius={PANEL_RADIUS} size="1.5cqw" />
        <CornerPlating position="bottom-right" color={`${GOLD} 0.6)`} radius={PANEL_RADIUS} size="1.5cqw" />

        <div className="flex flex-col min-h-0" style={{ maxHeight: 'calc(100cqh - 160px)' }}>
          <div className="min-h-0 overflow-y-auto medieval-scrollbar" style={{ padding: PANEL_PAD, paddingBottom: 0 }}>
            <div className="stagger-in flex items-center justify-between mb-4" style={{ '--stagger': '40ms' }}>
              <div className="flex items-center gap-2">
                <SlidersHorizontal className="size-3.5" style={{ color: ACCENT_GOLD }} />
                <span className="arena-heading text-sm font-bold" style={{ color: '#f4e4b8', textShadow: '0 0 12px rgba(180,140,60,0.18)' }}>{title}</span>
              </div>
              <button
                type="button"
                onClick={() => this.setOpen(false)}
                className="inline-flex items-center justify-center cursor-pointer transition-[background-color,color,scale] duration-100 active:scale-[0.96] hover:!bg-[rgba(180,60,60,0.35)] hover:!text-white"
                style={{ width: '2.5cqw', height: '2.5cqw', borderRadius: FIELD_RADIUS, background: 'transparent', color: 'rgba(215,200,150,0.7)' }}
                aria-label={t('Close deck filters')}
              >
                <X className="size-3" />
              </button>
            </div>

            <div className="flex flex-col gap-4">
              {hideElements ? null : (
              <div className="stagger-in" style={{ '--stagger': '80ms' }}>
                {fieldLabel(t('Elements'))}
                <div className="flex flex-wrap items-center gap-1">
                  <button
                    type="button"
                    {...chipProps((elements?.size || 0) === 0, PANEL_CHIP)}
                    onClick={() => this.props.onElementsChange?.(new Set())}
                  >
                    {t('All')}
                  </button>
                  {[k('Water'), k('Earth'), k('Fire'), k('Air')].map((el) => (
                    <button
                      key={el}
                      type="button"
                      {...chipProps(elements?.has(el), `flex items-center gap-1 ${PANEL_CHIP}`)}
                      onClick={() => this.toggleElement(el)}
                    >
                      <SorceryElementIcon element={el} className="size-3" />
                      {t(el)}
                    </button>
                  ))}
                </div>
              </div>
              )}

              <div className="stagger-in grid gap-x-4 gap-y-3" style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', '--stagger': '120ms' }}>
                {chipRow(t('Type'), [
                  { value: 'all', label: k('All') },
                  { value: 'constructed', label: k('Constructed') },
                  { value: 'peasant', label: k('Peasant') },
                  ...(isFreePlay() ? [] : [{ value: 'sandbox', label: k('Sandbox') }]),
                ], type, (v) => this.props.onTypeChange?.(v))}

                {chipRow(t('Sort'), sortOptions, sort, (v) => this.props.onSortChange?.(v))}
              </div>

              <div className="stagger-in grid gap-x-4 gap-y-3" style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', '--stagger': '160ms' }}>
                <div>
                  {fieldLabel(t('Avatar'))}
                  <Select
                    ariaLabel={t('Avatar filter')}
                    searchable
                    portalMenu
                    options={[{ value: '', label: t('Any Avatar') }, ...avatarOptions]}
                    value={avatar || ''}
                    onValueChange={(v) => this.props.onAvatarChange?.(v || null)}
                    placeholder={t('Any Avatar')}
                    menuSearchPlaceholder={t('Search avatars…')}
                    noOptionsMessage={t('No avatars')}
                    triggerStyle={FIELD_STYLE}
                  />
                </div>
                {hideTag ? null : (
                <div>
                  {fieldLabel(t('Tag'))}
                  <Select
                    ariaLabel={t('Tag filter')}
                    searchable
                    portalMenu
                    options={[
                      { value: 'all', label: t('Any Tag') },
                      { value: '__none__', label: t('Untagged') },
                      ...tagOptions.map((opt) => ({ value: opt.name, label: opt.name })),
                    ]}
                    value={tag}
                    onValueChange={(v) => this.props.onTagChange?.(v)}
                    placeholder={t('Any Tag')}
                    menuSearchPlaceholder={t('Search tags…')}
                    noOptionsMessage={t('No tags')}
                    triggerStyle={FIELD_STYLE}
                  />
                  {tagOptions.length > 0 ? (
                    <button
                      type="button"
                      className="mt-1 text-[0.5625cqw] font-semibold transition-colors cursor-pointer"
                      style={{ color: ACCENT_GOLD }}
                      onClick={() => { this.setOpen(false); onManageTags?.(); }}
                    >
                      {t('Manage Tags…')}
                    </button>
                  ) : null}
                </div>
                )}
              </div>
            </div>
          </div>

          <div className="stagger-in shrink-0 flex items-center justify-end gap-2" style={{ padding: PANEL_PAD, '--stagger': '200ms' }}>
            <Button type="button" variant="ghost" size="sm" onClick={this.handleReset} className="font-semibold active:scale-[0.96]" style={{ borderRadius: FIELD_RADIUS }}>{t('Reset')}</Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => this.setOpen(false)}
              className="font-bold active:scale-[0.96] !bg-[rgba(212,168,67,0.28)] hover:!bg-[rgba(212,168,67,0.45)] !text-[#f4e4b8]"
              style={{
                border: `1px solid ${GOLD} 0.55)`,
                borderRadius: FIELD_RADIUS,
                boxShadow: 'inset 0 1px 0 rgba(255,220,140,0.2), 0 0 12px rgba(212,168,67,0.22)',
              }}
            >
              {t('Done')}
            </Button>
          </div>
        </div>
      </motion.div>
    );
  }

  render() {
    const { open } = this.state;
    const { searchQuery, onSearchChange } = this.props;
    const activeCount = this.activeCount();

    return (
      <div
        ref={(el) => { this._rootEl = el; }}
        data-tutorial="deck-search"
        className="relative flex-1 min-w-0 max-w-[36cqw]"
      >
        <div
          className="flex items-stretch rounded-lg overflow-hidden transition-[border-color,box-shadow] duration-150"
          style={{
            height: '2.125cqw',
            background: '#080604',
            border: `1px solid ${GOLD} ${open ? '0.42' : '0.22'})`,
            boxShadow: open
              ? 'inset 0 1px 0 rgba(255,220,140,0.08), 0 0 14px rgba(212,168,67,0.14)'
              : 'inset 0 1px 0 rgba(255,220,140,0.03)',
          }}
        >
          <div className="relative flex-1 min-w-0 flex items-center">
            <svg
              className="absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none size-3.5"
              viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"
              style={{ color: `${GOLD} 0.5)` }}
            >
              <circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" />
            </svg>
            <input
              type="text"
              placeholder={t('Search decks…')}
              value={searchQuery}
              onInput={(e) => onSearchChange?.(e.target.value)}
              className="flex-1 min-w-0 h-full pl-9 pr-3 text-xs outline-none bg-transparent"
              style={{ color: TEXT_BODY }}
            />
          </div>

          <div className="w-px shrink-0" style={{ background: `${GOLD} 0.2)` }} />

          <button
            type="button"
            onClick={() => this.setOpen(!open)}
            aria-label={open ? t('Close deck filters') : t('Open deck filters')}
            title={open ? t('Close deck filters') : t('Deck filters')}
            className="relative shrink-0 h-full inline-flex items-center justify-center transition-all cursor-pointer"
            style={{
              padding: '0 0.75cqw',
              background: open ? `${GOLD} 0.18)` : 'transparent',
              color: open ? '#f4e4b8' : '#d7c896',
            }}
            onMouseEnter={(e) => { if (!open) e.currentTarget.style.background = 'rgba(212,168,67,0.08)'; }}
            onMouseLeave={(e) => { if (!open) e.currentTarget.style.background = 'transparent'; }}
          >
            <SlidersHorizontal className="size-3.5" />
            {activeCount > 0 ? (
              <span
                className="absolute inline-flex items-center justify-center rounded-full text-[0.5625cqw] font-bold tabular-nums"
                style={{
                  top: '0.25cqw', right: '0.125cqw', minWidth: '0.875cqw', height: '0.875cqw', padding: '0 0.1875cqw',
                  background: ACCENT_GOLD, color: '#1a1410',
                  boxShadow: '0 0 6px rgba(212,168,67,0.6), 0 0 0 1.5px #080604',
                }}
              >
                {activeCount}
              </span>
            ) : null}
          </button>
        </div>

        <AnimatePresence>
          {open ? this.renderPanel() : null}
        </AnimatePresence>
      </div>
    );
  }
}
